<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      {{ title }}
    </Header>
    <div class="my-5 mx-4">
      <p class="text-size-14 mb-5">
        自動音声にて通知された4桁の認証コードを入力してください
      </p>
      <SmsAuthForm
        v-model="latestPin"
        @input="onInputValidatePhoneAndTransition"
      />
      <div class="my-5 text-size-14">
        <p class="text-mild mb-3">数分待っても電話がかかってこない場合</p>
        <AppButton
          size="small"
          block
          rounded
          class="mb-3"
          @click="onClickReCall"
          data-testid="reCall"
        >
          再度電話を受ける
        </AppButton>
      </div>

      <SmsAuthWithPhoneSwitchContactForm />
    </div>
    <BlockBack />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import SmsAuthForm from "@/components/organisms/auth/SmsAuthForm.vue";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import { CreatePhoneRequest } from "@/types/auth/CreatePhoneRequest";
import { ValidatePhoneRequest } from "@/types/auth/ValidatePhoneRequest";
import SmsAuthWithPhoneSwitchContactForm from "@/components/organisms/auth/SmsAuthWithPhoneSwitchContactForm.vue";
import { showErrorModal } from "@/utils/showErrorModal";

@Component({
  components: {
    FeatherIcon,
    Header,
    SmsAuthForm,
    BlockBack,
    AppButton,
    SmsAuthWithPhoneSwitchContactForm
  }
})
export default class PhoneAuth extends Vue {
  @Prop({
    type: String,
    required: true
  })
  title!: string;

  @Prop({
    type: String,
    required: true
  })
  phoneNumber!: string | null;

  @Prop({
    type: Function,
    required: true
  })
  createPhoneRequest!: CreatePhoneRequest;

  @Prop({
    type: Function,
    required: true
  })
  validatePhoneRequest!: ValidatePhoneRequest;

  latestPin = "";

  get phoneNumberWithHyphen() {
    return formatPhoneNumber(this.phoneNumber);
  }

  async created() {
    await this.sendPhoneRequest();
  }

  async sendPhoneRequest() {
    if (!this.phoneNumber) throw new Error();
    const body = {
      phoneNumber: this.phoneNumber
    };
    try {
      await this.createPhoneRequest(body);
    } catch (e) {
      await showErrorModal(this, e as Error);
    }
  }

  async onClickReCall() {
    const result = await this.$bvModal.msgBoxConfirm(
      `自動の音声ガイダンスが流れ、4桁の数字を読み上げますので、お手元にメモをご用意の上、以下のボタンを押してください`,
      {
        bodyClass: "text-primary-dark",
        okTitle: "電話を受ける",
        cancelTitle: "キャンセル",
        cancelVariant: "gray-03",
        centered: true
      }
    );

    if (!result) return;
    await this.sendPhoneRequest();
  }

  phoneCodeValidation(latestPin: string) {
    if (!this.phoneNumber) throw new Error();
    const body = {
      latestPin,
      phoneNumber: this.phoneNumber
    };
    return this.validatePhoneRequest(body);
  }

  async onInputValidatePhoneAndTransition(latestPin: string) {
    // 4桁入力時のみ実行
    if (latestPin === null) return;
    if (latestPin.length !== 4) return;

    try {
      this.$loading.show();
      const { isVerified, smsSessionId } = await this.phoneCodeValidation(
        latestPin
      );
      if (!isVerified) return;
      this.$emit("validated", smsSessionId);
    } catch {
      await this.$bvModal.msgBoxOk("コードが正しくありません", {
        centered: true
      });
      this.latestPin = "";
    } finally {
      this.$loading.hide();
    }
  }
}
</script>
